/**
 * Que una versión nueva llegue sola al celular que ya está en el mercado.
 *
 * 🔴 EL PROBLEMA QUE RESUELVE, Y POR QUÉ NO ERA OBVIO.
 *
 * Esto no es una página: el navegador se guarda una copia de la aplicación (el service worker) y
 * la sirve sin preguntarle al servidor. Con `skipWaiting` y `clientsClaim` —ver `vite.config.ts`—
 * el worker nuevo se activa solo, pero **la pantalla que ya está abierta sigue ejecutando el
 * código viejo hasta que alguien recargue**. El 14-set se desplegaron los formularios de Softys
 * ocho horas antes de que abrieran diecinueve mercados, y la única forma de que la gente los viera
 * era decirle por WhatsApp «abrí, esperá y recargá». Una instrucción que hay que repetir a mano
 * cada vez que se publica algo **no es un mecanismo**: es una lista de gente a la que hay que
 * avisar, y siempre falta alguien.
 *
 * 🔴 POR QUÉ NO RECARGA APENAS SE ENTERA.
 *
 * Porque recargar tira lo que la persona tenga escrito. En esta app eso significa un puesto de
 * mercado a medio registrar, de pie en un pasillo, con la foto de la fachada ya tomada. Perder eso
 * es peor que quedarse con la versión vieja un rato más. Así que la versión nueva se aplica
 * **cuando no hay nada escrito en pantalla**, y si hay algo escrito espera: al terminar el
 * registro los campos se limpian y ahí entra sola.
 *
 * La comprobación mira el DOM y no un estado propio, a propósito: así ningún formulario nuevo
 * tiene que acordarse de avisar nada. Se equivoca hacia el lado seguro —un filtro con texto la
 * hace esperar de más— y eso está bien: **equivocarse diciendo «hay algo escrito» solo demora la
 * actualización; equivocarse al revés borra trabajo.**
 */

/** Cada cuánto se le pregunta al servidor si hay versión nueva. La app queda abierta la jornada
 *  entera sin navegar, así que sin esto no se entera nunca. */
const INTERVALO_BUSQUEDA_MS = 60_000;

/** Cada cuánto se vuelve a mirar si ya es seguro recargar, cuando había algo escrito. */
const INTERVALO_REINTENTO_MS = 5_000;

/**
 * ¿Hay algo escrito en algún campo de la pantalla?
 *
 * Vive exportada y con la raíz por parámetro para poder comprobarla caso por caso sin montar la
 * aplicación entera.
 */
export function hayAlgoEscrito(raiz: ParentNode = document): boolean {
  for (const campo of Array.from(raiz.querySelectorAll('input, textarea, select'))) {
    if (campo instanceof HTMLInputElement) {
      if (campo.type === 'checkbox' || campo.type === 'radio') {
        if (campo.checked) return true;
      } else if (campo.type === 'file') {
        if (campo.files && campo.files.length > 0) return true;
      } else if (campo.value.trim() !== '') {
        return true;
      }
    } else if (campo instanceof HTMLTextAreaElement) {
      if (campo.value.trim() !== '') return true;
    } else if (campo instanceof HTMLSelectElement) {
      // `selectedIndex > 0` y no `value !== ''`: un desplegable siempre tiene algo seleccionado,
      // así que lo único que distingue «eligió» de «está como vino» es no estar en la primera.
      if (campo.selectedIndex > 0) return true;
    }
  }
  return false;
}

export interface VigilanciaDeVersion {
  detener(): void;
}

export interface OpcionesDeVigilancia {
  /** Sustituible en las pruebas: recargar de verdad rompería el corredor de pruebas. */
  recargar?: () => void;
  /** Sustituible en las pruebas. Por defecto, que no haya nada escrito en pantalla. */
  esSeguro?: () => boolean;
  /** Sustituible en las pruebas para no esperar un minuto real. */
  intervaloBusquedaMs?: number;
  intervaloReintentoMs?: number;
}

/**
 * Arranca la vigilancia. Devuelve cómo detenerla.
 *
 * En un navegador sin service worker —o en las pruebas— no hace nada y no falla.
 */
export function iniciarVigilanciaDeVersion(
  opciones: OpcionesDeVigilancia = {},
): VigilanciaDeVersion {
  const recargar = opciones.recargar ?? (() => window.location.reload());
  const esSeguro = opciones.esSeguro ?? (() => !hayAlgoEscrito());
  const intervaloBusqueda = opciones.intervaloBusquedaMs ?? INTERVALO_BUSQUEDA_MS;
  const intervaloReintento = opciones.intervaloReintentoMs ?? INTERVALO_REINTENTO_MS;

  const sw =
    typeof navigator !== 'undefined' && 'serviceWorker' in navigator
      ? navigator.serviceWorker
      : null;
  if (!sw) return { detener() {} };

  // 🔴 EL SEGURO CONTRA EL BUCLE DE RECARGAS, Y ES LA LÍNEA MÁS IMPORTANTE DEL ARCHIVO.
  //
  // `controllerchange` NO significa solo «hay versión nueva». También se dispara la primera vez
  // que un service worker toma el control de una pestaña que se abrió sin ninguno —la primera
  // visita, o después de limpiar los datos del navegador—. Recargar en ese caso no actualiza
  // nada, y al recargar vuelve a no haber controlador al inicio: **recarga, vuelve a pasar,
  // recarga otra vez**. La aplicación queda inservible, y justamente en el celular de alguien que
  // está en un mercado.
  //
  // Si cuando cargó esta pestaña YA había un controlador, entonces un cambio de controlador solo
  // puede ser un worker nuevo reemplazando al anterior, que es exactamente lo que se busca.
  const habiaControlador = !!sw.controller;

  let versionNueva = false;
  let yaRecargando = false;
  let detenida = false;
  let timerBusqueda: ReturnType<typeof setInterval> | null = null;
  let timerReintento: ReturnType<typeof setTimeout> | null = null;

  const limpiarReintento = () => {
    if (timerReintento !== null) {
      clearTimeout(timerReintento);
      timerReintento = null;
    }
  };

  const aplicarSiSePuede = () => {
    if (detenida || yaRecargando || !versionNueva) return;
    if (!esSeguro()) {
      // Hay algo escrito. No se pierde la versión nueva: se vuelve a mirar en unos segundos, y
      // también cuando la persona vuelva a la pestaña.
      limpiarReintento();
      timerReintento = setTimeout(() => {
        timerReintento = null;
        aplicarSiSePuede();
      }, intervaloReintento);
      return;
    }
    yaRecargando = true;
    limpiarReintento();
    recargar();
  };

  const onControllerChange = () => {
    if (!habiaControlador) return;
    versionNueva = true;
    aplicarSiSePuede();
  };

  const buscar = () => {
    if (detenida) return;
    // Un fallo acá es lo normal sin señal. Se ignora: lo reintenta el propio intervalo.
    void sw.getRegistration().then((r) => r?.update()).catch(() => {});
  };

  const onVisibilidad = () => {
    if (document.visibilityState !== 'visible') return;
    // Volver a la pestaña es el mejor momento para las dos cosas: preguntar si hay versión nueva,
    // y aplicarla si quedó esperando.
    buscar();
    aplicarSiSePuede();
  };

  sw.addEventListener('controllerchange', onControllerChange);
  document.addEventListener('visibilitychange', onVisibilidad);
  timerBusqueda = setInterval(buscar, intervaloBusqueda);
  buscar();

  return {
    detener() {
      detenida = true;
      sw.removeEventListener('controllerchange', onControllerChange);
      document.removeEventListener('visibilitychange', onVisibilidad);
      if (timerBusqueda !== null) clearInterval(timerBusqueda);
      limpiarReintento();
    },
  };
}
